import { Box, Flex, Text } from '@chakra-ui/react';
import { FaPause } from '@react-icons/all-files/fa/FaPause';
import { FaPlay } from '@react-icons/all-files/fa/FaPlay';
import { useEffect, useMemo, useRef, useState } from 'react';

import colors from '../../chakra/foundations/colors';
import Button from '../../components/ui/buttons/Button';
import { VoiceTake } from '../../hooks/useVoiceRecorder';
import { formatSecondsAsTimer } from '../../utils/helpers';

const WAVEFORM_BAR_COUNT = 40;

type TakePlaybackProps = {
  take: Pick<VoiceTake, 'objectUrl' | 'durationSeconds'>;
};

const TakePlayback = ({ take }: TakePlaybackProps) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [litBarCount, setLitBarCount] = useState(0);

  const barHeights = useMemo(
    () => Array.from({ length: WAVEFORM_BAR_COUNT }, () => 8 + Math.round(Math.random() * 30)),
    [],
  );

  useEffect(() => {
    const audio = new Audio(take.objectUrl);
    audio.addEventListener('timeupdate', () => {
      setLitBarCount(
        Math.min(
          WAVEFORM_BAR_COUNT,
          Math.ceil((audio.currentTime / take.durationSeconds) * WAVEFORM_BAR_COUNT),
        ),
      );
    });
    audio.addEventListener('ended', () => {
      setIsPlaying(false);
      setLitBarCount(0);
      audio.currentTime = 0;
    });
    audioRef.current = audio;
    return () => audio.pause();
  }, [take]);

  const togglePlayback = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
      return;
    }
    audio
      .play()
      .then(() => setIsPlaying(true))
      .catch(() => setIsPlaying(false));
  };

  return (
    <Flex
      align="center"
      gap="14px"
      bg="white"
      border="1px solid"
      borderColor="platinum"
      borderRadius="14px"
      px={4}
      py={3.5}
      boxShadow={`0 3px 6px ${colors.boxShadowGray}`}
    >
      <Button
        variant="unstyled"
        aria-label={isPlaying ? 'Pause' : 'Play'}
        onClick={togglePlayback}
        w="46px"
        h="46px"
        minW="46px"
        borderRadius="full"
        bg="leaseEndBlue"
        color="white"
        fontSize="16px"
        display="flex"
        alignItems="center"
        justifyContent="center"
        _hover={{ bg: 'leaseEndBlueDark' }}
      >
        {isPlaying ? <FaPause /> : <FaPlay style={{ marginLeft: '3px' }} />}
      </Button>
      <Flex align="center" gap="3px" h="44px" flex={1} minW={0} overflow="hidden">
        {barHeights.map((barHeight, index) => (
          <Box
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            w="3.5px"
            minW="3.5px"
            borderRadius="full"
            h={`${barHeight}px`}
            bg={index < litBarCount ? 'leaseEndBlue' : 'gainsboro'}
          />
        ))}
      </Flex>
      <Text fontFamily="mono" fontSize="13px" color="grayLight" flexShrink={0}>
        {formatSecondsAsTimer(take.durationSeconds)}
      </Text>
    </Flex>
  );
};

export default TakePlayback;
